import React from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "./Projects";
import { Copy, Printer, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProposalDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { data: p } = useSWR(`/proposals/${id}`, fetcher);

  if (!p) return <div className="p-10 label-eyebrow text-zinc-500">{t("common.loading")}</div>;

  const shareUrl = `${window.location.origin}/proposal/${p.share_token}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("proposals.linkCopied"));
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="proposal-detail-page">
      <PageHeader
        eyebrow={`// PROPOSAL · ${p.id}`}
        title={p.title}
        sub={`${p.customer_name} · ${p.customer_address}`}
        action={
          <div className="flex items-center gap-3">
            <Badge status={p.status} />
            <Button data-testid="copy-share-link" onClick={copyLink} className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
              <Copy size={14} className="mr-2" /> {t("proposals.shareLink")}
            </Button>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <ArrowSquareOut size={14} className="mr-2" /> {lang === "fr" ? "Vue client" : "Customer view"}
              </Button>
            </a>
            <Button data-testid="print-proposal" variant="outline" onClick={() => window.print()} className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
              <Printer size={14} className="mr-2" /> PDF
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="mb-4 mono text-xs border-l-2 border-orange-600 bg-orange-50 px-4 py-2 break-all">
          {shareUrl}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-zinc-200">
            {p.scope && (
              <div className="px-5 py-4 border-b border-zinc-200">
                <p className="label-eyebrow text-zinc-500 mb-2">{t("proposals.scope")}</p>
                <p className="text-sm whitespace-pre-wrap">{p.scope}</p>
              </div>
            )}
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-6">Item</div><div className="col-span-2 text-right">Qty</div><div className="col-span-2 text-right">$</div><div className="col-span-2 text-right">{t("common.total")}</div>
            </div>
            {p.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm">
                <div className="col-span-6 font-medium">{l.label}<span className="ml-2 mono text-xs text-zinc-500">{l.unit}</span></div>
                <div className="col-span-2 text-right mono">{l.qty}</div>
                <div className="col-span-2 text-right mono">{fmt$(l.unit_price)}</div>
                <div className="col-span-2 text-right mono font-semibold">{fmt$(l.total)}</div>
              </div>
            ))}
            <div className="px-5 py-4 border-t border-zinc-200">
              <p className="label-eyebrow text-zinc-500 mb-2">{t("proposals.terms")}</p>
              <p className="text-sm whitespace-pre-wrap text-zinc-700">{p.terms}</p>
            </div>
          </div>
          <aside className="border border-zinc-200 h-fit">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">Summary</p></div>
            <div className="p-5 space-y-3">
              <SumRow k={t("common.subtotal")} v={fmt$(p.subtotal)} />
              <SumRow k={`Markup ${p.markup_pct}%`} v={fmt$(p.after_markup - p.subtotal)} />
              <SumRow k={`Tax ${p.tax_pct}%`} v={fmt$(p.total - p.after_markup)} />
              <div className="border-t border-zinc-300 pt-3">
                <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Total" : "Total"}</p>
                <p data-testid="proposal-total" className="font-display font-black text-3xl mt-2 text-orange-600 tracking-tighter">{fmt$(p.total)}</p>
              </div>
              <div className="border-t border-zinc-200 pt-3">
                <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Acompte" : "Deposit"} ({p.deposit_pct}%)</p>
                <p className="mono text-lg font-bold mt-1">{fmt$(p.deposit_amount)}</p>
              </div>
              {p.signed_name && (
                <div className="border-t border-green-200 pt-3 bg-green-50 -mx-5 -mb-5 px-5 py-3">
                  <p className="label-eyebrow text-green-700">{lang === "fr" ? "SIGNÉ PAR" : "SIGNED BY"}</p>
                  <p className="text-sm font-bold mt-1">{p.signed_name}</p>
                  <p className="mono text-xs text-zinc-600">{p.signed_at?.slice(0,19).replace("T", " ")}</p>
                </div>
              )}
            </div>
          </aside>
        </div>
      </PageBody>
    </div>
  );
}

function SumRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm border-b border-zinc-100 pb-2 last:border-b-0">
      <span className="label-eyebrow text-zinc-500">{k}</span>
      <span className="mono text-zinc-900">{v}</span>
    </div>
  );
}
